import React from "react";
import { Col, Divider, Form, InputNumber, Modal, Row, Select, Spin, theme } from "antd";
import { SUCCESS_COLOR } from "../../constants/color";
import { POST } from "../../frameworks/HttpClient";
import { URL_CHEMICAL, URL_SETTING, URL_TRUCK } from "../../constants/urls";
import ErrorMessages from "../../common/ErrorMessages";
import { currencyFormat, toFixed } from "../../frameworks/Util";
import { useParams } from "react-router-dom";
import useDropdownItem from "../../common/useDropdownItem";
import _ from "lodash";
import CustomDatePicker from "../../common/CustomDatePicker";
import { DISPENSE_CHEMICAL } from "../../constants/chemical";

export default function DispenseChemicalModal(props) {
  const params = useParams();
  const { open, onClose, target, onUpdate, seller} = props;
  const [loading, setLoading] = React.useState(false);
  const [ errorMessages, setErrorMessages ] = React.useState(null);
  const [debouncedSeller, setDebouncedSeller] = React.useState('');
  const [debouncedLotNH4, setDebouncedLotNH4] = React.useState('');
  const [debouncedLotTZ, setDebouncedLotTZ] = React.useState('');
  const [form] = Form.useForm();
  const watchTZ = Form.useWatch('tz_weight', form)
  const watchNH4 = Form.useWatch( 'nh4oh_weight', form)
  const isCreate = target === 'add'
  const isTruck = params.type === 'truck'

  const sellerDropdown = useDropdownItem({ 
    manual: true,
    url: URL_SETTING.SELLER_RAW_LATEX, 
    value:'id', 
    pagination: true, 
    params: { page_size: 50, 
      name : debouncedSeller
    }, 
      label_renderer: (item) => {return(`${item.name}`)
    }
  })

  const LotNH4Dropdown = useDropdownItem({
    manual: true,
    url: URL_CHEMICAL.QUALITY_CHEMICAL_TANK_TRANSACTION, 
    value:'id', 
    pagination: true, 
    params: { page_size: 50, 
      lot_no : debouncedLotNH4,
      is_closed : false,
    }, 
    label_renderer: (item) => {return(`${item.lot_no} - [${item.chemical_name}] : ${currencyFormat(item.remain_amount)}`)}
  })

  const LotTZDropdown = useDropdownItem({ 
    manual: true,
    url: URL_CHEMICAL.QUALITY_CHEMICAL_TANK_TRANSACTION, 
    value:'id', 
    pagination: true, 
    params: { page_size: 50, 
      lot_no : debouncedLotTZ,
      is_closed : false,
    }, 
      label_renderer: (item) => {return(`${item.lot_no} - [${item.chemical_name}] : ${currencyFormat(item.remain_amount)}`)
    }
  })

    const debounceSeller = React.useCallback(_.debounce((_searchVal) => { setDebouncedSeller(_searchVal) }, 800), []);
    const debounceLotNH4 = React.useCallback(_.debounce((_searchVal) => { setDebouncedLotNH4(_searchVal) }, 800), []);
    const debounceLotTZ = React.useCallback(_.debounce((_searchVal) => { setDebouncedLotTZ(_searchVal) }, 800), []);
  

  const {
    token: { colorWarning },
  } = theme.useToken();

  const handleOk = async() => {
    setLoading(true)
    setErrorMessages(null)
    try {
      const data = await form.validateFields();
      if(isTruck){
        data['truck_transaction'] = params.id
      }
      data['chemical_detail'] = [
        { chemical: DISPENSE_CHEMICAL.TZ, amount : toFixed(_.get(data, 'tz_weight', 0), 2), stock : _.get(data, 'tz', null) },
        { chemical: DISPENSE_CHEMICAL.NH4OH, amount : toFixed(_.get(data, 'nh4oh_weight', 0), 2), stock : _.get(data, 'nh4oh', null) },
      ]
      data['chemical_detail'] = _.filter(data['chemical_detail'], item => item.stock !== null);
      await POST(URL_TRUCK.CHEMICAL_TRANSFER, data)
      onUpdate();
    } catch (error) {
      if(error.message){
        setErrorMessages(error.message)
      } else {
        setErrorMessages(error.errorMessages)
      } 
    } finally {
      setLoading(false)
    }
  }

  const handleValueChange = async() => {
    try {
      const tz = form.getFieldValue('value_tz', 0) ? form.getFieldValue('value_tz') : 0
      const weight = form.getFieldValue('chemical_weight') ? form.getFieldValue('chemical_weight') : 0
      const weight_tz = tz * weight
      const weight_nh4oh = weight - weight_tz
      form.setFieldValue('tz_weight', weight_tz !== 0 ? toFixed(weight_tz, 3) : null)
      form.setFieldValue('nh4oh_weight', weight_nh4oh !== 0 ? toFixed(weight_nh4oh, 3) : null)  
    } catch (error) {
      setErrorMessages(error.errorMessages)
    }
  }

  React.useEffect(() => {
    if(open){
      setErrorMessages(null)
      sellerDropdown.fetchOptions();
      LotNH4Dropdown.fetchOptions();
      LotTZDropdown.fetchOptions();
      if(seller){
        form.setFieldValue('supplier', seller)
      }
      form.setFieldValue('value_tz' , 0.069)
    } else {
      form.resetFields();
    }
  },[open])


  React.useEffect(() => {
    if(open)
      sellerDropdown.fetchOptions();
  },[debouncedSeller])

  React.useEffect(() => {
    if(open)
      LotNH4Dropdown.fetchOptions();
  },[debouncedLotNH4])


  React.useEffect(() => {
    if(open)
      LotTZDropdown.fetchOptions();
  },[debouncedLotTZ])

	return(
    <Modal
      open={open}
      onCancel={onClose}
      okText={isCreate ? "ตกลง" : "แก้ไข"}
      okButtonProps={{ style: { backgroundColor : isCreate ? SUCCESS_COLOR : colorWarning}, loading: loading }}
      cancelText={"ไม่"}
      cancelButtonProps={{ loading: loading }}
      title={"จ่ายสารเคมีให้ผู้ขายน้ำยางสด"}
      width={600}
      onOk={handleOk}
    >
      <ErrorMessages message={errorMessages}/>
      <Form
        form={form}
        layout={'vertical'}
      >
        <Row>
          <Col span={24}>
            <Form.Item 
              label={'ชื่อผู้ขาย'} 
              style={{marginRight : 10}}
              name={"supplier"}
              rules={[
                {
                  required: true,
                  message: 'กรุณากรอกข้อมูล'
                },
              ]}
            >
              <Select 
                initialvalues={"-"}
                autoComplete="off" 
                placeholder="ชื่อผู้ขาย"
                showSearch
                filterOption={false}
                notFoundContent={sellerDropdown.loading ? <Spin size="small" /> : "-"}
                onSearch={debounceSeller}
                options={sellerDropdown.options}
                disabled={seller ? true : false}
                allowClear
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <CustomDatePicker
              label={'วันที่จ่ายสารเคมี'} 
              name={"date"}
              placeholder={'วันที่จ่ายสารเคมี'}
              rules={[
                {
                  required: true,
                  message: 'กรุณากรอกข้อมูล'
                },
              ]}
            />
          </Col>
          <Col span={8}>
            <Form.Item 
              label={'ค่า TZ'} 
              style={{marginRight : 10}}
              name={"value_tz"}
              rules = {[{ required : true, message: 'กรุณากรอกข้อมูล' }]}
            >
              <InputNumber key="value_tz" style={{ width : '100%' }} placeholder={'ค่า TZ'} onChange={handleValueChange}/>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item 
              label={'น้ำหนัก (Kg)'} 
              style={{marginRight : 10}}
              name={"chemical_weight"}
            >
              <InputNumber key="chemical_weight" style={{ width : '100%' }} placeholder={'น้ำหนัก (Kg)'} onChange={handleValueChange}/>
            </Form.Item>
          </Col>
        </Row>
        <Divider>NH4OH</Divider>
        <Row>
          <Col span={15}>
            <Form.Item 
              label={'Lot No'} 
              style={{marginRight : 10}}
              name={"nh4oh"}
              rules = {[{ required : watchNH4 ? true : false , message: 'กรุณากรอกข้อมูล' }]}
            >
              <Select 
                initialvalues={"-"}
                autoComplete="off" 
                placeholder="Lot No"
                showSearch
                filterOption={false}
                notFoundContent={LotNH4Dropdown.loading ? <Spin size="small" /> : "-"}
                onSearch={debounceLotNH4}
                options={LotNH4Dropdown.options.filter((option) => option.chemical_name.toLowerCase() === 'nh4oh')}
                allowClear
              />
            </Form.Item>
          </Col>
          <Col span={9}>
            <Form.Item 
              label={'ปริมาณการจ่าย (Kg)'} 
              style={{marginRight : 10}}
              name={"nh4oh_weight"}
              // rules = {[{ required : true, message: 'กรุณากรอกข้อมูล' }]}
            >
              <InputNumber style={{ width : '100%' }} placeholder={'ปริมาณการจ่าย (Kg)'}/>
            </Form.Item>
          </Col>
        </Row>
        <Divider>TZ</Divider>
        <Row>
        <Col span={15}>
            <Form.Item 
              label={'Lot No'} 
              style={{marginRight : 10}}
              name={"tz"}
              rules = {[{ required : watchTZ ? true : false, message: 'กรุณากรอกข้อมูล' }]}
            >
              <Select 
                initialvalues={"-"}
                autoComlete="off" 
                placeholder="Lot No"
                showSearch
                filterOption={false}
                notFoundContent={LotTZDropdown.loading ? <Spin size="small" /> : "-"}
                onSearch={debounceLotTZ}
                options={LotTZDropdown.options.filter((option) => option.chemical_name.toLowerCase() === 'tz')}
                allowClear
              />
            </Form.Item>
          </Col>
          <Col span={9}>
            <Form.Item 
              label={'ปริมาณการจ่าย (Kg)'} 
              style={{marginRight : 10}}
              name={"tz_weight"}
              // rules = {[{ required : true, message: 'กรุณากรอกข้อมูล' }]}
            >
              <InputNumber style={{ width : '100%' }} placeholder={'ปริมาณการจ่าย (Kg)'}/>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
)
}